import React, { useContext } from 'react';
import * as pt from 'prop-types';
import { Badge } from '@thd-olt-component-react/badge';
import {
  params, shape, client, string, alias, useDataModel
} from '@thd-nucleus/data-sources';
import { Image } from '@thd-olt-component-react/image';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import classNames from 'classnames';

// TODO: These should all be in a helper file or constant, not declared in the component
const BADGE_MODELS = {
  new: 'New',
  seasonal: 'New This Season',
  exclusive: 'Exclusive',
  trending: 'Trending',
  bestseller: 'Best Seller',
  custom: 'Custom',
  guaranteed: 'Guaranteed',
  'top rated': 'Top Rated',
  popular: 'Popular on Pinterest',
};

const INSPIRATIONAL_BADGE_MODELS = {
  new: 'New',
  trending: 'Trending',
  bestseller: 'Best Seller',
  exclusive: 'Exclusive',
};

const millisecondsPerDay = (1000 * 60 * 60 * 24);

function getBadge({ badges, inspirational }) {

  const productBadges = inspirational
    ? INSPIRATIONAL_BADGE_MODELS
    : BADGE_MODELS;
  const displayableBadges = Object.keys(productBadges);
  const itemBadges = badges
    .filter((badge) => displayableBadges.indexOf(badge?.name) > -1);
  return itemBadges.length ? itemBadges[0] : null;
}

function getEventBadge(badges) {
  const itemBadges = badges
    .filter((badge) => { return badge?.name === 'Event Badge'; });
  return itemBadges.length ? itemBadges[0] : null;
}

export function getTimerThreshold(timerDuration) {
  if (!timerDuration || (timerDuration <= millisecondsPerDay)) {
    return '0';
  }
  return millisecondsPerDay;
}

export function getDaysLeftThreshold(timerDuration) {
  if (!timerDuration || (timerDuration <= millisecondsPerDay)) {
    return '0';
  }
  return timerDuration;
}

function getEventTimerConfig(eventBadge) {
  const dateDisplay = (eventBadge.timerDuration === null || eventBadge.timerDuration === '0');
  return {
    endDate: eventBadge.endDate,

    timerThreshold: eventBadge.timer?.timeBombThreshold !== null
      ? eventBadge.timer?.timeBombThreshold
      : getTimerThreshold(eventBadge.timerDuration),

    daysLeftThreshold: eventBadge.timer?.daysLeftThreshold !== null
      ? eventBadge.timer?.daysLeftThreshold
      : getDaysLeftThreshold(eventBadge.timerDuration),

    dateDisplayThreshold: eventBadge.timer
      ? eventBadge.timer?.dateDisplayThreshold
      : (dateDisplay && eventBadge.endDate && (+Date.parse(eventBadge.endDate) - Date.now() + 1)),

    message: eventBadge?.timer?.message
      ? eventBadge.timer.message + ' '
      : null
  };
}

export function shouldDisplayTimer(eventBadge, config) {
  if (eventBadge?.timer?.timeBombThreshold
     || eventBadge?.timer?.daysLeftThreshold
     || eventBadge?.timer?.dateDisplayThreshold) {
    return true;
  }
  const offset = eventBadge.timerDuration ? +eventBadge.timerDuration : 0;
  const endDateMilliseconds = eventBadge.endDate && Date.parse(eventBadge.endDate);
  const isBeforeEndDate = (Date.now() <= endDateMilliseconds);
  const isAfterThreshold = (Date.now() + offset >= endDateMilliseconds);
  const isEndDateDisplay = config && config.dateDisplay;
  return !!(isBeforeEndDate && (isAfterThreshold || isEndDateDisplay));
}

export const ProductDetailsBadge = ({ storeId: propsStoreId, itemId, inspirational }) => {

  const { channel } = useContext(ExperienceContext);

  const storeIdCall = useStoreId();
  const storeId = propsStoreId || storeIdCall;

  const opts = {
    ssr: false,
    variables: { itemId, storeId }
  };
  const { data, loading, error } = useDataModel('clientOnlyProduct', opts);
  if ((error && !data) || loading || !data) {
    return <div className="product-details__placeholder" />;
  }

  const defaultBadge = (getBadge({ badges: data?.product?.badges || [], inspirational }));
  const eventBadge = (getEventBadge(data?.product?.badges || []));
  const timerConfig = eventBadge && getEventTimerConfig(eventBadge);
  const isMobileDisplayCrowded = (channel === 'mobile')
    && defaultBadge
    && eventBadge
    && timerConfig
    && shouldDisplayTimer(eventBadge, timerConfig);
  const wrapperClasses = classNames('product-details__badge-container-wrapper',
    { 'product-details__badge-container-wrapper--crowded': isMobileDisplayCrowded });

  if (!(defaultBadge || eventBadge)) {
    return <div className="product-details__placeholder" />;
  }

  return (
    <span className={wrapperClasses}>
      { defaultBadge && (
        <span className="product-details__badge-container">
          <Badge
            type="text"
            text={defaultBadge.label}
            color="#3e7697"
          />
        </span>
      )}
      { eventBadge && (
        <span className="product-details__badge-container product-details__badge-container--event">
          {eventBadge.creativeImageUrl && (channel !== 'mobile') && (
            <Image
              src={eventBadge.creativeImageUrl}
              alt="badge-image"
            />
          )}
          <Badge
            type="text"
            text={eventBadge.label}
            color={eventBadge.color || '#3e7697'}
          />
        </span>
      )}
      { eventBadge && timerConfig && shouldDisplayTimer(eventBadge, timerConfig)
          && (
            <span className="product-details__badge-container product-details__badge-container--timer">
              <CountdownTimer
                endDate={timerConfig.endDate}
                timebombThreshold={+timerConfig.timerThreshold}
                shouldUseDaysLeftDisplay
                shouldUseClockDisplay
                shouldUseDateDisplay
                shouldUseMessagingString
                shouldIncludeSvg
                daysLeftThreshold={+timerConfig.daysLeftThreshold}
                dateDisplayThreshold={+timerConfig.dateDisplayThreshold}
                customMessagingString={timerConfig.message}
              />
            </span>
          )}
    </span>
  );
};

ProductDetailsBadge.propTypes = {
  itemId: pt.string.isRequired,
  inspirational: pt.bool,
  storeId: pt.oneOfType([pt.string, pt.number])
};

ProductDetailsBadge.defaultProps = {
  inspirational: false,
  storeId: null
};

ProductDetailsBadge.dataModel = {
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    badges: client(
      params({ storeId: string() }).arrayOf(shape({
        color: string(),
        creativeImageUrl: string(),
        endDate: string(),
        label: string(),
        message: string(),
        name: string(),
        timerDuration: string(),
        timer: shape({
          timeBombThreshold: string(),
          daysLeftThreshold: string(),
          dateDisplayThreshold: string(),
          message: string()
        })
      }))
    )
  })
};

export default ProductDetailsBadge;
